// Libraries
import React from 'react';

// Supermove
import {IconSource} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {useDebouncedCallback, useEffect, useNavigationDOM} from '@supermove/hooks';
import {OrganizationModel, UserModel} from '@supermove/models';

// App
import {ChannelType, SSEEventType} from '@shared/modules/SSE/interfaces';
import {useSSEv2} from '@shared/modules/SSE/v2/hooks/useSSEv2';
import GlobalNavigationSidebarItem from 'modules/App/Global/components/GlobalNavigationSidebarItem';
import useAppContext from 'modules/App/context/useAppContext';

interface SidebarConversationsItemContentProps {
  name: string;
  selected: boolean;
  source: IconSource;
  to: string;
  organization: OrganizationModel;
  handleClose: () => void;
}

const POLL_INTERVAL = 1000 * 60 * 3; // 3 minutes

const SidebarConversationsItem = ({
  name,
  selected,
  source,
  to,
  organization,
  handleClose,
}: SidebarConversationsItemContentProps) => {
  const {navigator} = useNavigationDOM();
  const {isGlobalNavSidebarExpanded} = useAppContext();
  const {loading, data, refetch, startPolling, stopPolling} = useQuery<{
    viewer: UserModel;
  }>(SidebarConversationsItem.query, {
    fetchPolicy: 'cache-and-network',
  });

  const debounceTime = navigator.location.pathname === '/conversations' ? 1000 : 60_000;
  const debouncedRefetch = useDebouncedCallback(refetch, debounceTime);

  const {latestEvent, setChannelFilters, connected} = useSSEv2(
    [SSEEventType.COMMUNICATION_NEW],
    [],
  );

  useEffect(() => {
    if (data?.viewer.id) {
      setChannelFilters([
        {
          channelType: ChannelType.USER,
          channelId: data?.viewer.id,
        },
      ]);
    }
  }, [data?.viewer.id]);

  useEffect(() => {
    if (latestEvent && !loading && organization.features.isEnabledRealTimeMessages) {
      debouncedRefetch();
    }
  }, [latestEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!connected) {
      startPolling(POLL_INTERVAL);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [connected, startPolling, stopPolling]);

  return (
    <GlobalNavigationSidebarItem
      notificationCount={data?.viewer.openConversationsCount ?? 0}
      name={name}
      isSelected={selected}
      source={source}
      to={to}
      isExpanded={isGlobalNavSidebarExpanded}
      handleClose={handleClose}
      onPress={() => {}}
      isEnabledShowCountOnMinimizedSidebar={
        organization.features.isEnabledShowCountOnMinimizedSidebar
      }
    />
  );
};

SidebarConversationsItem.query = gql`
  query SidebarConversationsItem {
    ${gql.query}
    viewer {
      id
      openConversationsCount
    }
  }
`;

SidebarConversationsItem.fragment = gql`
  fragment SidebarConversationsItem_organization on Organization {
    id
    features {
      isEnabledRealTimeMessages: isEnabled(feature: "REAL_TIME_MESSAGES")
      isEnabledShowCountOnMinimizedSidebar: isEnabled(feature: "SHOW_COUNTS_ON_MINIMIZED_SIDEBAR")
    }
  }
`;

export {SidebarConversationsItem};
